import { useInfiniteQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getDeliveryRejections } from '../services/run-detail.service';
import {
  isCapabilityAvailable,
  isMissingRoute,
  markCapabilityUnavailable,
} from '../utils/catalog-capabilities';

/** Items read per page of the Delivery tab's rejections list. */
export const DELIVERY_REJECTIONS_PAGE_SIZE = 50;

/**
 * Finding 13 — one keyset page of a run's per-product delivery rejections, plus the whole run's
 * reason breakdown (B1, `feat/attention-reasons-and-delivery-rejections`). 404/405 on a deploy
 * without the route marks the `deliveryRejections` capability unavailable for the session, so the
 * Delivery tab's rejections section renders nothing rather than an error.
 */
export function useDeliveryRejections(runRef: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.catalogService.runDeliveryRejections(runRef),
    queryFn: async ({ pageParam }) => {
      if (!isCapabilityAvailable('deliveryRejections')) return null;
      try {
        return await getDeliveryRejections(runRef, {
          ...(pageParam ? { after: pageParam } : {}),
          size: DELIVERY_REJECTIONS_PAGE_SIZE,
        });
      } catch (error) {
        if (!isMissingRoute(error)) throw error;
        markCapabilityUnavailable('deliveryRejections');
        return null;
      }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page?.nextCursor ?? undefined,
    enabled: enabled && !!runRef,
  });
}
